<template>
    <div class="carouselBox" :class="className">
        <h2 v-if="url && title && products.length" class="carouselBox__title">
            <a :href="url" class="carouselBox__title-link">{{ title }}</a>
        </h2>
        <h2 v-else-if="title && products.length" class="carouselBox__title">
            {{ title }}
            <span v-if="stickerTitle" class="carouselBox__title-sticker">{{ stickerTitle }}</span>
        </h2>
        <Carousel
            v-if="products"
            :products="products"
            :type-item="typeItem"
            :show-pre-basket="showPreBasket"
            :item-list-name="itemListName"
            :carousel-name="title"
            :minimalized-product-visual="minimalizedProductVisual"
            :min-products-arrow-slider-count="minProductsArrowSliderCount"
            :show-availability="showAvailability"
            :addon-sale-behavior="addonSaleBehavior"
        />
        <div v-if="url && products.length" class="text-center mb-4 mt-1">
            <a :href="url" class="btn cta-button btn-outline-primary rounded-25">
                {{ $t('header.showText') }}
            </a>
        </div>
    </div>
</template>

<script lang="ts">
import Carousel from '~/components/elements/Carousel.vue'
import LuigisBoxEventApi from '~/extensions/LuigisBoxEventApi'
import { transformProductsToLuigisBoxItems } from '~/utils/transformProductsToLuigisBoxItems'
import Product from '~/models/Product/Product'

export default {
    components: {
        Carousel,
    },
    props: {
        title: {
            type: String,
            default: '',
        },
        url: {
            type: String,
            default: '',
        },
        className: {
            type: String,
            default: '',
        },
        products: {
            type: Array as PropType<Product[]>,
            required: true,
        },
        typeItem: {
            type: String,
            default: 'slim-carousel__item--product',
        },
        showPreBasket: {
            type: Boolean,
            default: true,
        },
        itemListName: {
            type: String,
            default: '',
        },
        refreshFrequentlyActualizedData: {
            type: Boolean,
            default: false,
        },
        minimalizedProductVisual: {
            type: Boolean,
            default: false,
        },
        luigisBoxCarouselType: {
            type: String,
            default: '',
        },
        luigisBoxItemIds: {
            type: String,
            default: '',
        },
        isLuigisBoxVariant: {
            type: Boolean,
            default: false,
        },
        stickerTitle: {
            type: String,
            default: '',
        },
        minProductsArrowSliderCount: {
            type: Number,
            default: 5,
        },
        showAvailability: {
            type: Boolean,
            default: true,
        },
        addonSaleBehavior: {
            type: Boolean,
            default: false,
        },
    },

    mounted() {
        if (this.refreshFrequentlyActualizedData) {
            refreshFrequentlyActualizedData(this.products)
        }

        if (this.luigisBoxCarouselType !== '') {
            LuigisBoxEventApi.sendRecommendationEvent(
                transformProductsToLuigisBoxItems(this.products),
                this.title,
                this.luigisBoxCarouselType,
                this.luigisBoxItemIds,
                this.isLuigisBoxVariant
            )
        }
    },
}
</script>

<style lang="scss" scoped>
/*
Structure:
    .carouselBox(.carouselBox--fullWidth|.carouselBox--dealDetail)
        .carouselBox__title
            .carouselBox__title-link
 */

.carouselBox {
    &--fullWidth {
        width: calc(100% + (2 * $padding-container-inside-mobile));
        margin-left: -$padding-container-inside-mobile;
        margin-bottom: 10px;

        @media only screen and (min-width: 576px) {
            width: calc(100% + (2 * $padding-container-inside));
            margin-left: -$padding-container-inside;
        }

        &:deep(.carouselBox__title) {
            margin-left: $padding-container-inside-mobile;

            @media (min-width: $max-width-container-inside) {
                margin-left: 6px;
            }
        }

        @media (min-width: $max-width-container-inside) {
            width: calc(100% + 12px); //margin on .product
            margin-left: -6px;
        }
    }

    &--dealDetail {
        .slim-carousel__item--product {
            @media (min-width: 992px) {
                flex: 0 0 calc(100% / 3.5);
                width: calc(100% / 3.5);
            }
            @media (min-width: $max-width-container-inside) {
                flex: 0 0 calc(100% / 4.5);
                width: calc(100% / 4.5);
            }
        }
    }

    &__title-sticker {
        background: $color-green-light;
        color: $color-green-dark;
        border-radius: 20px;
        display: inline-block;
        padding: 7px 20px;
        font-size: 14px;
        line-height: 20px;
    }
}

.carouselBox__title {
    font-size: 20px;
    line-height: 30px;
    color: $color-dark;
    text-align: left;
    margin-bottom: 0px;
    font-family: 'scale-variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans',
        'Droid Sans', 'Helvetica Neue', sans-serif;
    font-variation-settings: 'wght' 700, 'wdth' 100;
}

.carouselBox__title-link {
    color: $color-dark;
}
</style>
